/**
 * Peerless Lawn Care — Main App Orchestrator
 * Connects scrolling engines, custom cursor cursors, accordion panels,
 * count animations, and contact form submission actions.
 */

import { SmoothScroll } from './smooth-scroll.js';
import { initParallax } from './parallax.js';
// import { StarryNight } from './stars.js'; // Disabled to optimize scrolling performance

document.addEventListener('DOMContentLoaded', () => {
  
  // 1. Initialize Layout & Physics Modules
  const smoothScrollInstance = new SmoothScroll();
  initParallax();

  // Starry background loops are disabled to maximize framerate and scrolling smoothness

  // 4. Scroll Reveal Counters for Philosophy Stats
  const statsRow = document.querySelector('.stats-row');
  
  if (statsRow) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const counters = statsRow.querySelectorAll('.stat-number');
          
          counters.forEach(counter => {
            const target = parseInt(counter.getAttribute('data-value'), 10);
            const isPercent = counter.textContent.includes('%');
            const suffix = isPercent ? '%' : '';
            let startTimestamp = null;
            const duration = 1600; // Counter duration in milliseconds

            const countUpStep = (timestamp) => {
              if (!startTimestamp) startTimestamp = timestamp;
              const progress = Math.min((timestamp - startTimestamp) / duration, 1.0);
              
              // Easing function: Custom easeOutExpo
              const easedProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
              const currentValue = Math.floor(easedProgress * target);
              
              counter.textContent = `${currentValue}${suffix}`;

              if (progress < 1.0) {
                requestAnimationFrame(countUpStep);
              }
            };

            requestAnimationFrame(countUpStep);
          });

          // Unobserve after running once
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });

    observer.observe(statsRow);
  }

  const revealElements = document.querySelectorAll(
    '.philosophy-heading, .map-system-wrapper, .service-showcase-panel, .service-staggered-col, .why-us-card, .why-us-feedback-card'
  );

  revealElements.forEach(el => {
    el.classList.add('reveal-on-scroll');
  });

  const scrollObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in-view');
        // Unobserve after revealing to save CPU
        scrollObserver.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px' // Reveal slightly before crossing page line
  });

  revealElements.forEach(el => scrollObserver.observe(el));

  // 7. Anchor Smooth Link Routing & Hash Load Handler (Interops with SmoothScroll)
  const initSmoothRouting = () => {
    // Intercept same-page anchor link clicks
    const allLinks = document.querySelectorAll('a');
    allLinks.forEach(link => {
      const href = link.getAttribute('href');
      if (!href) return;

      try {
        const linkUrl = new URL(link.href, window.location.href);
        const isSamePage = linkUrl.pathname === window.location.pathname;

        if (isSamePage && linkUrl.hash) {
          const targetId = linkUrl.hash;
          const targetElement = document.querySelector(targetId === '#' ? 'body' : targetId);

          if (targetElement) {
            link.addEventListener('click', (e) => {
              e.preventDefault();

              const isSmoothActive = document.body.classList.contains('has-smooth-scroll') && typeof window.scrollCurrentY !== 'undefined';
              const activeScrollY = isSmoothActive ? window.scrollCurrentY : window.scrollY;
              const targetY = targetElement.getBoundingClientRect().top + activeScrollY;

              window.scrollTo({
                top: targetY,
                behavior: isSmoothActive ? 'auto' : 'smooth'
              });
            });
          }
        }
      } catch (err) {
        // Ignore parsing errors for custom schemes (tel:, mailto:)
      }
    });

    // Handle page load hash routing (e.g. cross-page navigation from suburb pages back to home)
    const hash = window.location.hash;
    if (hash) {
      const targetElement = document.querySelector(hash);
      if (targetElement) {
        // Prevent layout jumping during initial render
        const container = document.getElementById('scroll-container');
        if (container) {
          container.scrollTop = 0;
          container.scrollLeft = 0;
        }

        setTimeout(() => {
          const isSmoothActive = document.body.classList.contains('has-smooth-scroll') && typeof window.scrollCurrentY !== 'undefined';
          const activeScrollY = isSmoothActive ? window.scrollCurrentY : window.scrollY;
          const targetY = targetElement.getBoundingClientRect().top + activeScrollY;

          window.scrollTo({
            top: targetY,
            behavior: isSmoothActive ? 'auto' : 'smooth'
          });

          // Clean up the URL hash to keep the URL clean
          history.replaceState(null, null, ' ');
        }, 300);
      }
    }
  };

  initSmoothRouting();

  // 8. Dynamic Grid Visual Spring Mechanics (Desktop Only) - Cached Selector
  const horizontalLine = document.querySelector('.line-horizontal');
  if (horizontalLine) {
    window.addEventListener('smooth-scroll', (e) => {
      const velocity = e.detail.velocity;
      // Offset position proportionally to scroll velocity, with spring returning it to center (50%)
      const offset = 50 + (velocity * 0.05);
      horizontalLine.style.top = `${Math.max(35, Math.min(65, offset))}%`;
    });
  }

  // 9. Premium iOS Liquid-Glass Lead Form Submission & Interactions
  const initLeadForm = () => {
    const formContainer = document.querySelector('.hero-lead-form-container');
    const form = document.getElementById('hero-lead-form');
    const successContainer = document.getElementById('hero-lead-success');
    const submitButton = form ? form.querySelector('.form-submit-button') : null;
    const submitButtonText = submitButton ? submitButton.querySelector('span') : null;
    const backButton = document.getElementById('success-back-btn');

    if (!form || !successContainer) return;

    // Remove any existing error message
    const clearError = () => {
      const existingError = form.querySelector('.form-error-message');
      if (existingError) {
        existingError.remove();
      }
    };

    // Show error message
    const showErrorMsg = (msg) => {
      clearError();
      const errorDiv = document.createElement('div');
      errorDiv.className = 'form-error-message';
      errorDiv.textContent = msg;
      if (submitButton) {
        form.insertBefore(errorDiv, submitButton);
      } else {
        form.appendChild(errorDiv);
      }
    };

    // Validation definitions
    const validators = {
      name: (val) => val.trim().length > 0,
      address: (val) => val.trim().length > 0,
      phone: (val) => {
        const digits = val.replace(/\D/g, '');
        return digits.length >= 8;
      },
      email: (val) => {
        if (!val.trim()) return true; // Optional field
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(val.trim());
      }
    };

    // Update styling for field based on validation
    const validateField = (input, forceShow = false) => {
      const name = input.getAttribute('name');
      const val = input.value;
      const validator = validators[name];
      if (!validator) return true;

      const isValid = validator(val);
      const wrapper = input.closest('.form-input-wrapper');
      
      if (!wrapper) return isValid;

      const isTouched = input.dataset.touched === 'true' || forceShow;

      if (isTouched) {
        if (isValid) {
          wrapper.classList.remove('is-invalid');
          wrapper.classList.add('is-valid');
        } else {
          wrapper.classList.remove('is-valid');
          wrapper.classList.add('is-invalid');
        }
      } else {
        wrapper.classList.remove('is-valid', 'is-invalid');
      }

      return isValid;
    };

    // Set up real-time listener for inputs
    const inputsToValidate = form.querySelectorAll('.form-input');
    inputsToValidate.forEach(input => {
      // Mark as touched on blur
      input.addEventListener('blur', () => {
        input.dataset.touched = 'true';
        validateField(input);
      });

      // Update validation visual state on input if already touched
      input.addEventListener('input', () => {
        if (input.dataset.touched === 'true') {
          validateField(input);
        }
      });
    });

    const resetValidationStates = () => {
      inputsToValidate.forEach(input => {
        input.removeAttribute('data-touched');
        const wrapper = input.closest('.form-input-wrapper');
        if (wrapper) {
          wrapper.classList.remove('is-valid', 'is-invalid');
        }
      });
      clearError();
    };

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      clearError();

      // Run full validation check
      let isFormValid = true;
      let firstInvalidInput = null;

      inputsToValidate.forEach(input => {
        input.dataset.touched = 'true';
        const isValid = validateField(input, true);
        if (!isValid) {
          isFormValid = false;
          if (!firstInvalidInput) {
            firstInvalidInput = input;
          }
        }
      });

      if (!isFormValid) {
        if (firstInvalidInput) {
          firstInvalidInput.focus();
        }
        return;
      }

      if (submitButton) {
        submitButton.disabled = true;
        if (submitButtonText) submitButtonText.textContent = 'Processing coordinates...';
      }

      const formData = new FormData(form);
      const urlEncodedData = new URLSearchParams(formData).toString();

      const isLocal = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' || 
                      window.location.hostname === '::1' || 
                      window.location.hostname.endsWith('.local') || 
                      window.location.protocol === 'file:';

      fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: urlEncodedData
      })
      .then(response => {
        if (response.ok) {
          // Switch screens beautifully on success
          formContainer.classList.add('submitted');
          successContainer.classList.add('active');
          form.reset();
          resetValidationStates();
        } else {
          throw new Error(`Server responded with status ${response.status}`);
        }
      })
      .catch(error => {
        console.error('Form submission error:', error);
        if (isLocal) {
          console.warn('[Netlify Dev Simulation] Local development environment detected. Bypassing network error and simulating success screen.');
          formContainer.classList.add('submitted');
          successContainer.classList.add('active');
          form.reset();
          resetValidationStates();
        } else {
          showErrorMsg('Sorry, there was an issue submitting your request. Please try again or call 0484 911 777.');
        }
      })
      .finally(() => {
        if (submitButton) {
          submitButton.disabled = false;
          if (submitButtonText) submitButtonText.textContent = 'Get My Free Estimate';
        }
      });
    });

    if (backButton) {
      backButton.addEventListener('click', () => {
        formContainer.classList.remove('submitted');
        successContainer.classList.remove('active');
        resetValidationStates();
      });
    }
  };

  initLeadForm();

  // 10. Call / SMS Modal Pop-up Controller
  const initContactModal = () => {
    const modal = document.getElementById('contact-modal');
    const closeBtn = modal ? modal.querySelector('.modal-close-btn') : null;
    const copyBtn = modal ? modal.querySelector('.phone-copy-btn') : null;
    const copyIcon = copyBtn ? copyBtn.querySelector('.copy-icon') : null;
    const checkIcon = copyBtn ? copyBtn.querySelector('.check-icon') : null;

    if (!modal) return;

    // Open Modal (handles both hero button and header actions phone button)
    const triggerBtns = document.querySelectorAll('#hero-btn-call, .btn-portal');
    triggerBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        modal.classList.add('active');
      });
    });

    // Close Modal via Close button
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        modal.classList.remove('active');
      });
    }

    // Close Modal via background click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.classList.remove('active');
      }
    });

    // Clipboard Copy Action
    if (copyBtn) {
      copyBtn.addEventListener('click', () => {
        const phoneNumber = '0484 911 777';
        navigator.clipboard.writeText(phoneNumber).then(() => {
          // Visual success state: swap copy icon with check icon
          if (copyIcon && checkIcon) {
            copyIcon.style.display = 'none';
            checkIcon.style.display = 'block';

            setTimeout(() => {
              copyIcon.style.display = 'block';
              checkIcon.style.display = 'none';
            }, 2000);
          }
        }).catch(err => {
          console.error('Failed to copy text: ', err);
        });
      });
    }
  };

  initContactModal();

  // 11. Interactive 3x3 Photo Gallery Grid with FLIP Expansion Transition
  const initGalleryGrid = () => {
    const container = document.querySelector('.gallery-container');
    const activeView = document.querySelector('.gallery-active-view');
    const activeImg = activeView ? activeView.querySelector('img') : null;
    const activeCaption = document.querySelector('.gallery-active-caption');
    const closeBtn = document.querySelector('.gallery-close-btn');
    const items = document.querySelectorAll('.gallery-item');
    
    if (!container || !activeView || !closeBtn) return;
    
    let lastClickedItem = null;
    
    items.forEach(item => {
      item.addEventListener('click', () => {
        lastClickedItem = item;
        const img = item.querySelector('img');
        const overlaySpan = item.querySelector('.gallery-item-overlay span');
        if (!img) return;
        
        // Update expanded view content
        if (activeImg) {
          activeImg.src = img.src.replace('w=600', 'w=1200'); // Load higher resolution image for detailed view
          activeImg.alt = img.alt;
        }
        if (activeCaption && overlaySpan) {
          activeCaption.textContent = overlaySpan.textContent;
        }
        
        // Calculate item offsets and sizes relative to parent container
        const itemRect = item.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const top = itemRect.top - containerRect.top;
        const left = itemRect.left - containerRect.left;
        const width = itemRect.width;
        const height = itemRect.height;
        
        // A. Position the active view exactly above the clicked thumbnail with transition disabled
        activeView.style.transition = 'none';
        activeView.style.top = `${top}px`;
        activeView.style.left = `${left}px`;
        activeView.style.width = `${width}px`;
        activeView.style.height = `${height}px`;
        
        // Force a layout reflow (style recalculation) to apply properties immediately
        activeView.offsetHeight;
        
        // B. Apply transitions and expand coordinates to fill the whole parent container
        activeView.style.transition = '';
        activeView.classList.add('active');
        activeView.style.top = '0px';
        activeView.style.left = '0px';
        activeView.style.width = '100%';
        activeView.style.height = '100%';
      });
    });
    
    const closeGallery = () => {
      if (!lastClickedItem) {
        activeView.classList.remove('active');
        return;
      }
      
      const itemRect = lastClickedItem.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const top = itemRect.top - containerRect.top;
      const left = itemRect.left - containerRect.left;
      const width = itemRect.width;
      const height = itemRect.height;
      
      // Animate active view back to original thumbnail size
      activeView.classList.remove('active');
      activeView.style.top = `${top}px`;
      activeView.style.left = `${left}px`;
      activeView.style.width = `${width}px`;
      activeView.style.height = `${height}px`;
      
      // Clear dimensions after transition completes (0.5s match CSS)
      setTimeout(() => {
        if (!activeView.classList.contains('active')) {
          activeView.style.width = '0px';
          activeView.style.height = '0px';
        }
      }, 500);
    };
    
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      closeGallery();
    });
  };

  initGalleryGrid();

  // 12. FAQ Accordion Controller
  const initFaqAccordion = () => {
    const faqItems = document.querySelectorAll('.faq-item');
    faqItems.forEach(item => {
      const button = item.querySelector('.faq-question-btn');
      if (!button) return;
      button.addEventListener('click', () => {
        const isActive = item.classList.contains('active');
        
        // Single-open accordion: close other items
        faqItems.forEach(i => {
          if (i !== item) {
            i.classList.remove('active');
            const btn = i.querySelector('.faq-question-btn');
            if (btn) btn.setAttribute('aria-expanded', 'false');
          }
        });

        item.classList.toggle('active');
        button.setAttribute('aria-expanded', !isActive ? 'true' : 'false');
        
        // Trigger scroll height recalculations
        window.dispatchEvent(new CustomEvent('content-update'));
      });
    });
  };

  initFaqAccordion();

  // 13. Inquire Now Button - Lead Form Flasher
  const initInquireFlash = () => {
    const inquireBtn = document.querySelector('.btn-get-started');
    const formContainer = document.querySelector('.hero-lead-form-container');
    if (!inquireBtn || !formContainer) return;

    inquireBtn.addEventListener('click', () => {
      formContainer.classList.remove('form-flash-active');
      // Trigger layout reflow to restart css animation
      void formContainer.offsetWidth;
      formContainer.classList.add('form-flash-active');

      setTimeout(() => {
        formContainer.classList.remove('form-flash-active');
      }, 850);
    });
  };

  initInquireFlash();
});

